import React, { useEffect, useId, useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { ChevronDown, ChevronsUpDown, Clock, X } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { Calendar } from 'src/components/ui/calendar';
import { Label } from 'src/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import TimeColumnPicker from 'src/components/common/TimeColumnPicker';
import { cn } from 'src/lib/utils';

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

// "HH:mm" (24-hour) -> { hour: '1'-'12', minute: '00'-'59', period }.
// Empty/undefined time yields blank hour/minute so the fields start empty
// rather than defaulting to a misleading 12:00.
const to12Hour = (time) => {
  if (!time) return { hour: '', minute: '', period: 'AM' };
  const [h, m] = time.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return { hour: String(hour12), minute: String(m).padStart(2, '0'), period };
};

// hour ('1'-'12') + minute + period -> "HH:mm" (24-hour), the shape callers
// (ActionPayloadModal, actionPayloadSchemas' buildInterviewSlotPayload)
// already expect from this component's onTimeChange.
const to24Hour = (hour, minute, period) => {
  let h = Number(hour) % 12;
  if (period === 'PM') h += 12;
  return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

// Paired date + time capture, adapted from shadcn's DatePickerTime block —
// a Popover-anchored Calendar for the date half. The time half diverges
// from the reference (and from a plain native <input type="time">): a
// native time input's 12-hour-vs-24-hour display follows the browser/OS
// locale, which this app can't control, and Ahedge's users need the AM/PM
// clearly visible regardless of that setting — so the time half speaks
// explicit 12-hour + AM/PM throughout instead. It still speaks "HH:mm"
// 24-hour to the outside world (same as the native input's own value
// shape) — onTimeChange/time are unchanged, so callers that already
// consume this component don't need to know the display is 12-hour
// underneath.
//
// The time half's picker itself (TimeColumnPicker) has been through two
// later design changes, both per explicit reference screenshots. First an
// analog clock face (hour face, then a minute face) replaced typed
// hour/minute fields; that was then replaced again by the current
// TimeColumnPicker — three always-visible, independently-scrollable
// columns (hour / minute / AM-PM), no face-switching needed since nothing
// is hidden. Minutes are offered in 15-minute stops (00/15/30/45), per
// that same reference — a precision reduction from the original
// free-typed-any-minute input this eventually replaced, specifically
// requested despite that (see TimeColumnPicker.jsx for the full history).
//
// Two other things differ from the reference: this codebase doesn't have
// shadcn's Field/FieldGroup/FieldLabel primitives or the Base UI `render`
// trigger prop (swapped for this app's own Label and the classic Radix
// `asChild` trigger pattern already used in JoiningDetailsModal.jsx), and
// this version is fully controlled — the reference keeps its own useState
// with no way for a parent form to ever read the picked value back out,
// which makes it a demo, not something a real form can use. date/time and
// their onDateChange/onTimeChange live with the caller instead.
const DateTimePicker = ({
  date,
  time,
  onDateChange,
  onTimeChange,
  dateLabel,
  timeLabel,
  disabled,
  minDate,
  className,
}) => {
  const [open, setOpen] = useState(false);
  // Time half's own Popover open state — separate from the date half's
  // `open` above so opening one never affects the other. Per explicit
  // design reference: the always-visible hour/minute inputs + AM/PM toggle
  // now live behind a single collapsed trigger (clock icon, formatted
  // value or a "--:-- --" placeholder, a clear button once a time is set,
  // and a chevron) instead of sitting inline.
  const [timeOpen, setTimeOpen] = useState(false);
  const dateId = useId();
  const timeId = useId();

  const [hour, setHour] = useState(() => to12Hour(time).hour);
  const [minute, setMinute] = useState(() => to12Hour(time).minute);
  const [period, setPeriod] = useState(() => to12Hour(time).period);

  // Re-sync from an externally-changed `time` (e.g. the parent form
  // resetting to {} when the modal reopens).
  useEffect(() => {
    const next = to12Hour(time);
    setHour(next.hour);
    setMinute(next.minute);
    setPeriod(next.period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time]);

  // Commits to the parent only once both hour and minute are filled in.
  const commit = (h, m, p) => {
    if (h === '' || m === '') return;
    onTimeChange(to24Hour(clamp(Number(h), 1, 12), clamp(Number(m), 0, 59), p));
  };

  // TimeColumnPicker hands back a plain number for hour (1-12) or minute
  // (0/15/30/45), or 'AM'/'PM' for period — each column commits its own
  // value independently the moment it's clicked, leaving the popover open
  // so the other columns stay reachable (see handleClearTime and the
  // header comment for why nothing here auto-closes it).
  const selectHour = (value) => {
    const h = String(value);
    setHour(h);
    commit(h, minute, period);
  };

  const selectMinute = (value) => {
    const m = String(value).padStart(2, '0');
    setMinute(m);
    commit(hour, m, period);
  };

  const handlePeriodChange = (nextPeriod) => {
    setPeriod(nextPeriod);
    commit(hour, minute, nextPeriod);
  };

  const handleClearTime = () => {
    setHour('');
    setMinute('');
    setPeriod('AM');
    onTimeChange('');
  };

  // The trigger's own displayed value — deliberately read from the
  // committed `time` prop (via to12Hour) rather than the live hour/minute
  // local state above, so it only ever shows a real, fully-committed time.
  // While the popover is open and the user is mid-edit (picked an hour but
  // not yet a minute, say), the trigger stays on whatever was last
  // committed instead of flashing a half-picked value.
  const committed = to12Hour(time);
  const formattedTime = time
    ? `${committed.hour.padStart(2, '0')}:${committed.minute} ${committed.period}`
    : '';

  return (
    <div className={cn('flex gap-3', className)}>
      <div className="flex-1 space-y-2">
        {dateLabel && <Label htmlFor={dateId}>{dateLabel}</Label>}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              id={dateId}
              disabled={disabled}
              className="w-full justify-between font-normal"
            >
              {date ? format(date, 'PPP') : 'Select date'}
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              defaultMonth={date}
              disabled={minDate ? { before: minDate } : undefined}
              onSelect={(value) => {
                onDateChange(value);
                setOpen(false);
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
      <div className="space-y-2">
        {timeLabel && <Label htmlFor={timeId}>{timeLabel}</Label>}
        <div className="relative">
          <Popover open={timeOpen} onOpenChange={setTimeOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                id={timeId}
                disabled={disabled}
                className="flex h-9 w-[168px] items-center gap-2 rounded-md border border-input bg-background pl-3 pr-3 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span
                  className={cn(
                    'flex-1 text-left tabular-nums',
                    !formattedTime && 'text-muted-foreground'
                  )}
                >
                  {formattedTime || '--:-- --'}
                </span>
                {/* Reserves the clear button's width so the value/placeholder
                    text never sits underneath it once one is rendered. */}
                {formattedTime && <span className="w-3.5" aria-hidden="true" />}
                <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2" align="start">
              <TimeColumnPicker
                hour={hour}
                minute={minute}
                period={period}
                onSelectHour={selectHour}
                onSelectMinute={selectMinute}
                onSelectPeriod={handlePeriodChange}
                onClose={() => setTimeOpen(false)}
                disabled={disabled}
              />
            </PopoverContent>
          </Popover>
          {/* A real sibling button layered on top of the trigger (not
              nested inside it) — clicking it fires only this button's own
              onClick rather than also toggling the popover, without needing
              stopPropagation/nested-interactive-element workarounds. */}
          {formattedTime && !disabled && (
            <button
              type="button"
              aria-label="Clear time"
              onClick={handleClearTime}
              className="absolute right-8 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground-strong"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

DateTimePicker.propTypes = {
  // JS Date or undefined/null — the calendar's own selected-day shape.
  date: PropTypes.instanceOf(Date),
  // "HH:mm", 24-hour, e.g. "14:30" — same shape a native <input type="time">
  // would give back; the 12-hour AM/PM display is purely presentational.
  time: PropTypes.string,
  onDateChange: PropTypes.func.isRequired,
  onTimeChange: PropTypes.func.isRequired,
  dateLabel: PropTypes.node,
  timeLabel: PropTypes.node,
  disabled: PropTypes.bool,
  // Blocks any day before this one, e.g. `new Date()` to disallow the past.
  minDate: PropTypes.instanceOf(Date),
  className: PropTypes.string,
};

DateTimePicker.defaultProps = {
  date: undefined,
  time: '',
  dateLabel: 'Date',
  timeLabel: 'Time',
  disabled: false,
  minDate: undefined,
  className: undefined,
};

export default DateTimePicker;
